import { Controller } from '@hotwired/stimulus'
import { broadcastInputChange } from '../_broadcast_change_utils'

export default class extends Controller<HTMLDivElement> {
  static targets = [
    'trigger',
    'dropdown',
    'option',
    'tagTemplate',
    'selectAll',
    'deselectAll',
    'zeroValueInput',
    'search'
  ]

  declare readonly triggerTarget: HTMLButtonElement
  declare readonly dropdownTarget: HTMLDivElement
  declare readonly hasDropdownTarget: boolean
  declare readonly optionTargets: HTMLDivElement[]
  declare readonly tagTemplateTarget: HTMLTemplateElement
  declare readonly selectAllTarget: HTMLButtonElement
  declare readonly deselectAllTarget: HTMLButtonElement
  declare readonly zeroValueInputTarget: HTMLInputElement
  declare readonly hasZeroValueInputTarget: boolean
  declare readonly searchTarget: HTMLInputElement
  declare readonly hasSearchTarget: boolean

  static values = {
    isMulti: Boolean,
    hasMultiTags: Boolean,
    sendOnChange: Boolean,
    isDynamic: Boolean,
    hasSearch: Boolean,
    placeholder: String,
    blankOption: String,
    name: String
  }

  declare readonly hasIsMultiValue: boolean
  declare readonly isMultiValue: boolean
  declare readonly hasHasMultiTagsValue: boolean
  declare readonly hasMultiTagsValue: boolean
  declare readonly hasPlaceholderValue: boolean
  declare readonly placeholderValue: string
  declare readonly hasBlankOptionValue: boolean
  declare readonly blankOptionValue: string
  declare readonly hasSendOnChangeValue: boolean
  declare readonly sendOnChangeValue: boolean
  declare readonly hasIsDynamicValue: boolean
  declare readonly isDynamicValue: boolean
  declare readonly hasHasSearchValue: boolean
  declare readonly hasSearchValue: boolean
  declare readonly nameValue: string

  initialHeight = 0
  optionsForSearch: string[] = []

  connect(): void {
    this.#changeZeroValueInputActiveState()

    if (this.hasSearchValue) {
      this.optionsForSearch = this.optionTargets.map(optionElement => {
        return optionElement.textContent.trim().toLowerCase()
      })
    }
    if (this.isDynamicValue) {
      const form = this.triggerTarget.form
      form?.addEventListener('ui-input:populate', e => {
        const event = e as CustomEvent<{ labels: string[]; fieldName?: string }>
        const { labels, fieldName } = event.detail
        if (fieldName !== undefined && fieldName !== this.nameValue) return
        this.optionsForSearch = labels.map(label => label.trim().toLowerCase())
      })
    }
  }

  /* HELPERS */

  #hideDropdown(): void {
    this.dropdownTarget.hidePopover()
    window.removeEventListener('click', this.#hideDropdownOnClickOutside.bind(this))
  }

  #hideDropdownOnClickOutside(e: MouseEvent): void {
    if (!this.hasDropdownTarget) return
    const isInDropdown =
      this.dropdownTarget.contains(e.target as Node) || this.dropdownTarget === e.target
    const isInTrigger =
      this.triggerTarget.contains(e.target as Node) || this.triggerTarget === e.target
    if (!isInDropdown && !isInTrigger) {
      this.#hideDropdown()
      window.removeEventListener('click', this.#hideDropdownOnClickOutside.bind(this))
    }
  }

  #showDropdown(): void {
    this.dropdownTarget.showPopover()
    this.#resizeAndRepositionDropdown()
    requestAnimationFrame(() => {
      window.addEventListener('scroll', this.#hideDropdown.bind(this), { once: true })
      window.addEventListener('click', this.#hideDropdownOnClickOutside.bind(this))
      if (this.hasSearchTarget) this.searchTarget.focus()
    })
  }

  #changeZeroValueInputActiveState(): void {
    if (!this.hasZeroValueInputTarget) return
    const checkedInputs = this.element.querySelectorAll('input:checked')
    const hasValues = checkedInputs.length > 0

    if (hasValues) {
      this.zeroValueInputTarget.setAttribute('disabled', '')
    } else {
      this.zeroValueInputTarget.removeAttribute('disabled')
    }
  }

  #changeTriggerHtmlContent(htmlContent: string): void {
    const triggerTextElement = this.triggerTarget?.firstElementChild as
      | HTMLDivElement
      | null
      | undefined
    if (triggerTextElement !== undefined && triggerTextElement !== null) {
      triggerTextElement.innerHTML = htmlContent
    }
  }

  #currentlyCheckedValues(): string[] {
    const checkedValues: string[] = []
    this.optionTargets.forEach(optionElement => {
      const inputElement = optionElement.querySelector(
        'input[type="checkbox"], input[type=radio]'
      ) as HTMLInputElement
      if (
        inputElement?.nextElementSibling === null ||
        inputElement?.nextElementSibling === undefined
      ) {
        return
      }
      const optionLabel =
        inputElement?.value !== '' ? inputElement.nextElementSibling?.innerHTML.trim() : ''
      if (
        inputElement !== null &&
        inputElement.checked &&
        optionLabel !== '' &&
        optionLabel !== null
      ) {
        checkedValues.push(optionLabel ?? '')
      }
    })
    return checkedValues
  }

  #resizeAndRepositionDropdown(): void {
    if (!this.hasDropdownTarget) return
    const MARGIN_THRESHOLD = 50
    const MINIMUM_SPACE = 180

    // resize dropdown to trigger width
    if (this.dropdownTarget.clientWidth < this.triggerTarget.offsetWidth) {
      this.dropdownTarget.style.width = `${this.triggerTarget.offsetWidth}px`
    }

    // reposition dropdown
    const triggerRect = this.triggerTarget.getBoundingClientRect()
    this.dropdownTarget.style.top = `${triggerRect.bottom}px`
    this.dropdownTarget.style.left = `${triggerRect.left}px`
    const spaceForDropdown = window.innerHeight - this.triggerTarget.getBoundingClientRect().bottom
    const spaceForDropup = this.triggerTarget.getBoundingClientRect().top
    // if dropdown has enough space, extend its height so it can show more of the content
    this.dropdownTarget.style.maxHeight = `${spaceForDropdown - MARGIN_THRESHOLD}px`

    // if dropdown is too much on the bottom, reposition it on top of the trigger and adjust height again
    if (spaceForDropdown < MINIMUM_SPACE && spaceForDropup > MINIMUM_SPACE) {
      this.dropdownTarget.style.maxHeight = `${spaceForDropup - MARGIN_THRESHOLD}px`
      this.dropdownTarget.style.top = `${triggerRect.top - this.dropdownTarget.clientHeight}px`
    }
  }

  #textWithStrippedHtml(html: string): string {
    const doc = new DOMParser().parseFromString(html, 'text/html')
    return doc.body.textContent ?? ''
  }

  #renderedTagForMulti(value: string): string {
    const serializer = new XMLSerializer()
    const clonedTag = this.tagTemplateTarget.content
    if (clonedTag.firstElementChild !== null) {
      const contentPlace = clonedTag.querySelector('em')
      if (contentPlace !== null) contentPlace.textContent = this.#textWithStrippedHtml(value)
    }
    const clonedTagHtmlAsString = serializer.serializeToString(this.tagTemplateTarget.content)
    return clonedTagHtmlAsString
  }

  #siblingOption(isPrev = false): HTMLInputElement | null {
    const visibleOptionElements = this.optionTargets.filter(
      option => !option.classList.contains('hidden')
    )
    const optionFocusedIndex = visibleOptionElements.findIndex(
      optionElement => optionElement.querySelector('input') === document.activeElement
    )
    const optionElementToFocus = (): HTMLDivElement => {
      if (optionFocusedIndex === -1) {
        return visibleOptionElements[isPrev ? visibleOptionElements.length - 1 : 0]
      }
      return visibleOptionElements[optionFocusedIndex + (isPrev ? -1 : 1)]
    }
    return optionElementToFocus()?.querySelector('input') ?? null
  }

  #toggleSelectAllButtons(): void {
    const isAllSelected = this.optionTargets.every(
      optionElement => optionElement.querySelector('input')?.checked
    )
    if (isAllSelected) {
      this.selectAllTarget.classList.add('hidden')
      this.deselectAllTarget.classList.remove('hidden')
    } else {
      this.selectAllTarget.classList.remove('hidden')
      this.deselectAllTarget.classList.add('hidden')
    }
  }

  #selectAllOnMulti(isInverted = false): void {
    this.optionTargets.forEach(optionElement => {
      const isChecked = optionElement?.querySelector('input')?.checked
      const comparisonReference = isInverted
      if (isChecked === comparisonReference) {
        optionElement.querySelector('input')?.click()
      }
    })
  }

  /* DROPDOWN EVENTS */

  clickTrigger(e: MouseEvent): void {
    if (!this.hasDropdownTarget) return

    e.preventDefault()
    if (this.dropdownTarget.matches(':popover-open')) {
      this.#hideDropdown()
    } else {
      this.#showDropdown()
    }
  }

  /* SELECT EVENTS */

  clickSelectAllOnMulti(e: MouseEvent): void {
    e.preventDefault()
    this.#selectAllOnMulti()
  }

  clickDeselectAllOnMulti(e: MouseEvent): void {
    e.preventDefault()
    this.#selectAllOnMulti(true)
  }

  optionSelected(e: InputEvent): void {
    const checkedValues = this.#currentlyCheckedValues()
    let triggerHtml = ''
    if (this.isMultiValue && this.hasMultiTagsValue && checkedValues.length > 0) {
      checkedValues.forEach(value => {
        triggerHtml += this.#renderedTagForMulti(value)
      })
    } else if (checkedValues.length > 0) {
      triggerHtml = checkedValues.join(', ')
    } else if (this.hasPlaceholderValue && this.placeholderValue !== '') {
      triggerHtml = `<span class="text-300 font-bold tracking-wide">${this.placeholderValue}</span>`
    } else if (this.hasBlankOptionValue) {
      triggerHtml = `<span class="opacity-75">${this.blankOptionValue}</span>`
    } else {
      triggerHtml = '&nbsp;'
    }
    this.#changeTriggerHtmlContent(triggerHtml)
    if (!this.isMultiValue) {
      this.#hideDropdown()
    } else {
      this.#toggleSelectAllButtons()
    }
    this.#changeZeroValueInputActiveState()
    broadcastInputChange(e.currentTarget as HTMLInputElement)

    if (this.hasSendOnChangeValue && this.sendOnChangeValue) {
      this.triggerTarget.form?.submit()
    }
  }

  /* TAG EVENTS */

  clickToRemoveTagOnMulti(e: MouseEvent): void {
    e.stopImmediatePropagation()
    e.preventDefault()
    const value = (e.target as HTMLElement).parentElement?.parentElement?.querySelector(
      'em'
    )?.innerText
    this.optionTargets.forEach(optionElement => {
      if (optionElement.innerText.trim() === value?.trim()) {
        optionElement.querySelector('input')?.click()
      }
    })
  }

  /* SEARCH EVENTS */

  search(e: InputEvent & { target: HTMLInputElement }): void {
    const searchTerm = e.target?.value
    // reset
    this.optionTargets.forEach(optionElement => {
      optionElement?.classList.remove('hidden')
    })
    if (searchTerm !== undefined && searchTerm !== null && searchTerm !== '') {
      // if for some reason the search reference array is not populated, we should do it in the slow way
      if (this.optionsForSearch.length === 0) {
        this.optionTargets.forEach(optionElement => {
          if (
            optionElement.innerText.toLowerCase().includes(searchTerm.toLowerCase()) ||
            searchTerm === ''
          ) {
            optionElement?.classList.remove('hidden')
          } else {
            optionElement?.classList.add('hidden')
          }
        })
      } else {
        // else we do it in the fast way
        const foundIndexes = this.optionsForSearch.reduce<number[]>((acc, option, index) => {
          if (option.includes(searchTerm.toLowerCase())) {
            acc.push(index)
          }
          return acc
        }, [])
        this.optionTargets.forEach((optionElement, index) => {
          const trueIndex =
            this.hasBlankOptionValue && !this.isMultiValue && this.isDynamicValue
              ? index - 1
              : index
          if (foundIndexes.includes(trueIndex)) {
            optionElement?.classList.remove('hidden')
          } else {
            optionElement?.classList.add('hidden')
          }
        })
      }
    }
  }

  searchKeydown(e: KeyboardEvent): void {
    if (e.key === 'Escape') {
      this.#hideDropdown()
    }
  }

  // keys: ESC and Enter

  keydown(e: KeyboardEvent): void {
    switch (e.key) {
      case 'Escape':
        this.#hideDropdown()
        break

      case 'Enter':
        e.preventDefault()
        if (e.target !== null && e.target instanceof HTMLInputElement) {
          e.target.click()
        }
        break

      case 'ArrowDown':
        e.preventDefault()
        this.#siblingOption()?.focus()
        this.#showDropdown()
        break

      case 'ArrowUp':
        e.preventDefault()
        this.#siblingOption(true)?.focus()
        break
    }
  }
}
